import { X, Plus, ArrowLeftRight, Trash2, LogOut } from 'lucide-react';
import { Moon, Sun } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useTranslation } from '../../../i18n/i18n';
import { USER_MODAL_TYPES } from '../../../components/ShiftSettingsModal/constants';

const getInitials = (name) => {
  if (!name) return '?';

  const parts = name.trim().split(' ');
  const first = parts[0]?.charAt(0) || '';
  const second = parts[1]?.charAt(0) || '';
  return (first + second).toUpperCase();
};

/**
 * SideMenu component for the FeedTimestamping page
 * Displays options for user switching, theme toggling, and logout
 * Also shows the currently active user at the bottom
 */
const SideMenu = ({
  showSideMenu,
  setShowSideMenu,
  uiMode,
  updateUiMode,
  onOpenUserModal,
  setConfirmLogout,
  currentUser,
  shiftInfo,
  settings,
}) => {
  const { t } = useTranslation(currentUser?.info?.languageCode);

  // Get the active user's name from settings based on shiftInfo.user
  const getActiveUserName = () => {
    if (shiftInfo?.user && settings?.users && settings.users[shiftInfo.user]) {
      const user = settings.users[shiftInfo.user];
      return `${user.firstname} ${user.lastname}`;
    }
    return currentUser?.displayName || currentUser?.email || t('common_user');
  };

  return (
    <div
      className={cn(
        'fixed top-0 left-0 w-full h-full z-[1000] opacity-0 invisible transition-opacity duration-300 ease-in-out',
        showSideMenu && 'opacity-100 visible',
      )}
    >
      <div
        className={cn(
          'side-menu fixed top-0 -right-[26.25rem] w-[26.25rem] h-full z-[1001] transition-[right] duration-300 ease-in-out overflow-y-auto flex flex-col bg-[#FAFAFA] dark:bg-[#0C121D] shadow-[-2px_0_10px_rgba(0,0,0,0.25)] dark:shadow-[-4px_0_4px_0_rgba(0,0,0,0.25)] border-l border-l-[#E8E8EA] dark:border-l-[#3C3E53] rounded-l-xl',
          showSideMenu && 'right-0',
        )}
      >
        <div className='flex justify-between items-center py-4 px-6 border-b  border-b-[#E8E8EA] dark:border-b-[#242933] box-border'>
          <h2 className='text-xl font-semibold text-[#16192C] dark:text-[#FAFAFA]'>{t('side_menu_title')}</h2>
          <button onClick={() => setShowSideMenu(false)}>
            <X className='size-5 text-[#8A8C95] hover:text-[#5C5E6B] active:text-[#5C5E6B] dark:text-[#90A1B9] dark:hover:text-[#CAD5E2] dark:active:text-[#CAD5E2]' />
          </button>
        </div>

        <div className='p-6 flex-1'>
          <div className='flex items-center justify-between gap-1.5 mb-8'>
            <span className='text-lg leading-[120%] font-medium text-[#16192C] dark:text-[#FAFAFA]'>
              {t('side_menu_mode')}
            </span>

            <div className='flex items-center p-1 gap-0.5 rounded-full border border-[#E8E8EA] dark:border-[#3C3E53] bg-[#E8E8EA] dark:bg-[#0C121D]'>
              <button
                className={cn(
                  'flex items-center justify-center py-2 px-4 rounded-full text-[#5C5E6B] dark:text-[#FAFAFA]',
                  uiMode === 'light-mode' &&
                    'bg-[#2563EB] dark:bg-[#3D8DD4] border border-[#95CEFF] dark:border-[#95CEFF] text-white dark:text-[#FAFAFA]',
                )}
                onClick={() => updateUiMode('light-mode')}
                aria-label='Light Mode'
                aria-pressed={uiMode === 'light-mode'}
              >
                <Sun className='size-4' />
              </button>

              <button
                className={cn(
                  'flex items-center justify-center py-2 px-4 rounded-full text-[#5C5E6B] dark:text-[#FAFAFA]',
                  uiMode === 'dark-mode' &&
                    'bg-[#2563EB] dark:bg-[#3D8DD4] border border-[#95CEFF] dark:border-[#95CEFF] text-white dark:text-[#FAFAFA]',
                )}
                onClick={() => updateUiMode('dark-mode')}
                aria-label='Dark Mode'
                aria-pressed={uiMode === 'dark-mode'}
              >
                <Moon className='size-4' />
              </button>
            </div>
          </div>

          <button
            type='button'
            className='flex items-center gap-3 min-h-10 w-full mb-3 dark:hover:bg-[#1A202E] dark:active:bg-[#0C121D] hover:bg-[#F5F5F5] active:bg-[#E1E1E1] rounded px-1 py-2'
            onClick={() => {
              setShowSideMenu(false);
              onOpenUserModal(USER_MODAL_TYPES.SWITCH);
            }}
          >
            <ArrowLeftRight className='size-5 shrink-0 mr-3 text-[#5C5E6B] dark:text-[#CAD5E2]' />
            <span className='text-lg leading-[120%] font-medium text-[#5C5E6B] dark:text-[#CAD5E2]'>
              {t('side_menu_switch_user')}
            </span>
          </button>

          <button
            type='button'
            className='flex items-center gap-3 min-h-10 w-full mb-3 dark:hover:bg-[#1A202E] dark:active:bg-[#0C121D] hover:bg-[#F5F5F5] active:bg-[#E1E1E1] rounded px-1 py-2'
            onClick={() => {
              setShowSideMenu(false);
              onOpenUserModal(USER_MODAL_TYPES.ADD);
            }}
          >
            <Plus className='size-5 shrink-0 mr-3 text-[#5C5E6B] dark:text-[#CAD5E2]' />
            <span className='text-lg leading-[120%] font-medium text-[#5C5E6B] dark:text-[#CAD5E2]'>
              {t('side_menu_add_user')}
            </span>
          </button>

          <button
            type='button'
            className='flex items-center gap-3 min-h-10 w-full dark:hover:bg-[#1A202E] dark:active:bg-[#0C121D] hover:bg-[#F5F5F5] active:bg-[#E1E1E1] rounded px-1 py-2'
            onClick={() => {
              setShowSideMenu(false);
              onOpenUserModal(USER_MODAL_TYPES.DELETE);
            }}
          >
            <Trash2 className='size-5 shrink-0 mr-3 text-[#DA1E28] dark:text-[#E04B4B]' />
            <span className='text-lg leading-[120%] font-medium text-[#DA1E28] dark:text-[#E04B4B]'>
              {t('side_menu_delete_user')}
            </span>
          </button>
        </div>

        {/* Active user display at the bottom */}
        <div className='py-4 px-6 flex gap-3 items-center border-t border-t-[#E8E8EA] dark:border-t-0'>
          <div className='flex items-center gap-3 flex-1'>
            <div className='size-9 rounded-full bg-[#D0D1D5] dark:bg-[#2E3440] border border-[#B9BAC0] dark:border-[#3C3E53] flex items-center justify-center text-[#2D3041] dark:text-[#CAD5E2] text-base leading-normal font-semibold uppercase'>
              {getInitials(getActiveUserName())}
            </div>
            <div className='flex min-w-0 flex-col gap-0.5'>
              <div className='truncate text-base leading-[120%] font-semibold text-[#2D3041] dark:text-[#FAFAFA]'>
                {getActiveUserName()}
              </div>
              <div className='truncate text-sm leading-[120%] text-[#8A8C95] dark:text-[#CAD5E2]'>
                {t('side_menu_active_user')}
              </div>
            </div>
          </div>

          <button
            className='flex items-center gap-2 min-h-12 py-2 px-3.5 text-[#DA1E28] dark:text-[#E04B4B]'
            onClick={() => {
              setShowSideMenu(false);
              setConfirmLogout(true);
            }}
          >
            <LogOut className='size-[1.125rem] shrink-0' />
            <span className='text-base leading-[120%] font-medium'>{t('common_logout')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SideMenu;
